/**
 * Semantic design tokens, shadcn-style: a fixed set of named color roles per
 * color mode, emitted as CSS custom properties on `:root` (light) and
 * `.dark` (dark).
 *
 * Values are plain CSS color strings (hex, hsl() or oklch()). Presets store
 * full values; per-tenant overrides replace individual roles with a CSS color
 * string. The generator (theme-to-css.ts) turns a resolved theme into a
 * `<style>` payload and maps the roles onto:
 *
 * - the storefront token names (--background, --primary, ...) so custom
 *   storefront CSS can opt into the tenant palette;
 * - @medusajs/ui variables (--bg-base, --fg-interactive, ...) so the whole
 *   store recolors without touching components;
 * - the site-blocks variables (--site-accent, --site-bg-*) so Puck blocks
 *   pick the theme up without any changes to the blocks themselves.
 */

/** One color mode (light or dark) worth of semantic tokens. */
export type ColorModeTokens = {
  /** Page background. Maps to --bg-base. */
  background: string
  /** Default text color. Maps to --fg-base. */
  foreground: string
  /** Card / component surface. Maps to --bg-component. */
  card: string
  cardForeground: string
  /** Popover / flyout surface. Maps to --bg-field. */
  popover: string
  popoverForeground: string
  /** Interactive accent: links, buttons, --fg-interactive, --site-accent. */
  primary: string
  /** Text or icon color on top of primary. Maps to --fg-on-color. */
  primaryForeground: string
  /** Hover shade for primary. Maps to --fg-interactive-hover. */
  primaryHover: string
  /** Secondary surface: chip backgrounds, muted sections (--site-bg-muted). */
  secondary: string
  secondaryForeground: string
  /** Subtle surface. Maps to --bg-subtle. */
  muted: string
  /** De-emphasized text. Maps to --fg-subtle and --fg-muted. */
  mutedForeground: string
  /** Highlight surface (rows, hovers). */
  accent: string
  accentForeground: string
  /** Danger color. */
  destructive: string
  destructiveForeground: string
  /** Hairline borders. Maps to --border-base. */
  border: string
  /** Form field borders. */
  input: string
  /** Focus ring color. */
  ring: string
  /** High-contrast band behind sections. Maps to --site-bg-accent. */
  contrast: string
  /** Text on top of the contrast band. */
  contrastForeground: string
}

/** Everything a preset (or a stored tenant theme) can define. */
export type ThemeDefinition = {
  /** Key from the preset registry, e.g. "default" or "cyberpunk". */
  preset?: string
  /** Per-mode role overrides applied on top of the preset. */
  overrides?: {
    light?: Partial<ColorModeTokens>
    dark?: Partial<ColorModeTokens>
  }
  /** Corner radius scale in rem (0 = square, 0.5 = Medusa default). */
  radius?: number
  /** Font key from FONTS, resolved to --font-sans (the body font). */
  font?: string
  /** Heading font key from FONTS, resolved to --font-heading. */
  headingFont?: string
  /** Key from FONT_PAIRS, expands to headingFont + font in one pick. */
  fontPair?: string
  /**
   * Режим разработки: витрина заменяет форму регистрации заглушкой, а
   * server action signup отклоняет попытки. По умолчанию включено
   * (отсутствие ключа = true), выключается явным false в админке.
   */
  registrationClosed?: boolean
  /**
   * Чекаут для незарегистрированных (гостей): витрина показывает гостю
   * заглушку вместо формы оформления, а server action placeOrder отклоняет
   * попытку. Зарегистрированные покупатели оформляют заказ как обычно.
   * Режим разработки: по умолчанию включено (отсутствие ключа = true),
   * выключается явным false в админке.
   */
  guestCheckoutClosed?: boolean
}

export type ThemePreset = {
  name: string
  label: string
  light: ColorModeTokens
  dark: ColorModeTokens
  radius: number
  font: string
  headingFont?: string
}

export const DEFAULT_RADIUS = 0.5

/**
 * Default preset: exactly what the storefront shipped before theming, i.e.
 * the Medusa UI light palette (zinc) with the WCAG-fixed interactive pair
 * from globals.css (blue-600 / blue-700 instead of blue-500/600).
 * Value convention here is shadcn HSL channels without the hsl() wrapper.
 */
export const defaultPreset: ThemePreset = {
  name: "default",
  label: "Default",
  light: {
    background: "0 0% 100%",
    foreground: "240 5.9% 10%",
    card: "0 0% 98%",
    cardForeground: "240 5.9% 10%",
    popover: "0 0% 98%",
    popoverForeground: "240 5.9% 10%",
    primary: "221.2 83.2% 53.3%",
    primaryForeground: "0 0% 100%",
    primaryHover: "224.3 76.3% 48%",
    secondary: "240 4.9% 95.9%",
    secondaryForeground: "240 5.9% 10%",
    muted: "0 0% 98%",
    mutedForeground: "240 5.2% 33.9%",
    accent: "240 4.8% 95.9%",
    accentForeground: "240 5.9% 10%",
    destructive: "0 84.2% 60.2%",
    destructiveForeground: "0 0% 98%",
    border: "240 5.9% 89.8%",
    input: "240 4.9% 89.8%",
    ring: "221.2 83.2% 53.3%",
    contrast: "240 5.9% 10%",
    contrastForeground: "0 0% 100%",
  },
  dark: {
    background: "240 2.8% 13.5%",
    foreground: "240 4.8% 95.9%",
    card: "240 3.7% 16.1%",
    cardForeground: "240 4.8% 95.9%",
    popover: "240 3.7% 16.1%",
    popoverForeground: "240 4.8% 95.9%",
    primary: "217.2 91.2% 59.8%",
    primaryForeground: "0 0% 98%",
    primaryHover: "217.2 91.2% 64.9%",
    secondary: "240 3.7% 15.9%",
    secondaryForeground: "240 4.8% 95.9%",
    muted: "240 3.7% 15.1%",
    mutedForeground: "240 5% 64.9%",
    accent: "240 3.7% 15.9%",
    accentForeground: "240 4.8% 95.9%",
    destructive: "0 62.8% 30.6%",
    destructiveForeground: "240 4.8% 95.9%",
    border: "240 3.7% 15.9%",
    input: "240 3.7% 15.9%",
    ring: "217.2 91.2% 59.8%",
    contrast: "0 0% 98%",
    contrastForeground: "240 5.9% 10%",
  },
  radius: 0.5,
  font: "inter",
}

export type ResolvedTheme = {
  light: ColorModeTokens
  dark: ColorModeTokens
  radius: number
  font: string
  headingFont: string
}
